import { Fragment, useEffect, useRef, useState } from "react";
import LinkedInTag from 'react-linkedin-insight';

import { FadeInSlideUp, Icon, IconTypes, Logo, OutlineText, ThoughtBubble, Ticker } from "../components";
import {
    BottomRowPodcastCovers, CoverImage, FreshDataSticker, FreshWithDataBackground,
    FreshWithDataLeaf, FreshWithDataOrange, Headphones, Heart, LocallySourcedSticker,
    Microphones, ParagraphsCoverImage, ParagraphsCoverImageMobile, PublisherAdswizz,
    PublisherEdison, PublisherPandora, PublisherPodsurvey, PublisherSignalHill,
    PublisherSimplecast, PublisherStitcher, Sticker2022, TopRowPodcastCovers,
} from "../images";
import { Brands, Breakpoints, isEven, linkedInPixelTrack, range, sendFormSubmission, wait } from "../utils";

import "../styles/Intro.css";

const hosts = [
    {
        brand: Brands.SXM_MEDIA,
        color: "neon-tangerine",
        text: "The gateway for marketers to the largest digital audio advertising ecosystem in North America, from satellite radio to streaming audio to podcasts.",
    },
    {
        brand: Brands.ADSWIZZ,
        color: "solid-indigo",
        text: "The technology engine powering the monetization of audio content internationally.",
    },
    {
        brand: Brands.SIMPLECAST,
        color: "solid-purple",
        text: "The creator-first podcast technology platform powering the next generation of audio stars, providing the tools they need to grow, understand, and better connect with their audiences.",
    },
];
const hostLogoMobileHeights = {
    [Brands.SXM_MEDIA]: 48,
    [Brands.ADSWIZZ]: 24,
    [Brands.SIMPLECAST]: 25,
};
const publishers = [
    {
        title: "First Party Platform Data",
        backgroundFill: "gradient-rose-peach-2",
        brands: [PublisherStitcher, PublisherPandora, PublisherAdswizz],
    },
    {
        title: "Exclusive Studies",
        backgroundFill: "gradient-plum-plum",
        brands: [PublisherSignalHill],
    },
    {
        title: "Insider Surveys",
        backgroundFill: "gradient-blue-blue",
        brands: [PublisherSimplecast, PublisherPodsurvey],
    },
    {
        title: "3rd Party Reports",
        backgroundFill: "gradient-tangerine-gold",
        brands: [PublisherEdison],
    },
];
const countries = ["Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa", "Andorra", "Angola", "Anguilla", "Antarctica", "Antigua and Barbuda", "Argentina", "Armenia", "Aruba", "Asia/Pacific Region", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Bouvet Island", "Brazil", "British Indian Ocean Territory", "British Virgin Islands", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde", "Caribbean Netherlands", "Cayman Islands", "Central African Republic", "Chad", "Chile", "China", "Christmas Island", "Cocos (Keeling) Islands", "Colombia", "Comoros", "Congo", "Cook Islands", "Costa Rica", "Cote d'Ivoire", "Croatia", "Cuba", "Curaçao", "Cyprus", "Czech Republic", "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Europe", "Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France", "French Guiana", "French Polynesia", "French Southern and Antarctic Lands", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Gibraltar", "Greece", "Greenland", "Grenada", "Guadeloupe", "Guam", "Guatemala", "Guernsey", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Heard Island and McDonald Islands", "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Isle of Man", "Israel", "Italy", "Jamaica", "Japan", "Jersey", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macau", "Macedonia (FYROM)", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Montserrat", "Morocco", "Mozambique", "Myanmar (Burma)", "Namibia", "Nauru", "Nepal", "Netherlands", "Netherlands Antilles", "New Caledonia", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Niue", "Norfolk Island", "North Korea", "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Pitcairn Islands", "Poland", "Portugal", "Puerto Rico", "Qatar", "Réunion", "Romania", "Russia", "Rwanda", "Saint Barthélemy", "Saint Helena", "Saint Kitts and Nevis", "Saint Lucia", "Saint Martin", "Saint Pierre and Miquelon", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Sint Maarten", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Georgia and the South Sandwich Islands", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Svalbard and Jan Mayen", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo", "Tokelau", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Turks and Caicos Islands", "Tuvalu", "U.S. Virgin Islands", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "United States Minor Outlying Islands", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Wallis and Futuna", "Western Sahara", "Yemen", "Zambia", "Zimbabwe"];
const industryTypes = ["Agency", "Brand/Advertiser", "Trading Desk", "Advertising Network/Exchange", "Content Aggregator", "Music Streaming Service", "News/Talk Publisher", "Gaming Publisher", "Podcast Publisher/Network", "Independent Podcaster", "Radio Broadcaster", "Application Developer", "Infrastructure Provider", "Content Publishing Service Provider", "Press/Journalist", "3rd Party Vendor"];
const currentPodcastCover = 0;
const playbackLineLength = 275;
const scrollSpeed = 0.5;

const RequiredStar = () => (
    <span className="required-star">*</span>
);

const Dropdown = ({ label, options, placeholderText, setValue, value }) => (
    <div className="dropdown-wrapper">
        <label className="gt-america-regular-18-24">
            {label}<RequiredStar />
        </label>
        <select
            className={value === "" ? "" : "active"}
            defaultValue=""
            onChange={(e) => setValue(e.target.value)}
        >
            <option value="" disabled>
                {placeholderText}
            </option>
            {options.map((option, index) => (
                <option key={index} value={option}>
                    {option}
                </option>
            ))}
        </select>
    </div>
);

const RadioSelect = ({ isCheckbox = false, label, options, setValue, smallText, value }) => (
    <div className="radio-select-wrapper">
        {label && (
            <div className="input-title gt-america-regular-18-24">
                {label}<RequiredStar />
            </div>
        )}
        {options.map((option, index) => (
            <label
                className={`radio-group ${option === value ? "active" : ""} ${smallText ? "gt-america-regular-7-9" : "gt-america-regular-14-16"}`}
                key={index}
            >
                <div className="custom-radio-button">
                    <input
                        name={label}
                        onChange={(e) => setValue(isCheckbox ? e.target.checked : option)}
                        type={isCheckbox ? "checkbox" : "radio"}
                    />
                    <div className="square-radio">
                        <div className="inner-square"></div>
                    </div>
                </div>
                <span className={`radio-label ${smallText ? "radio-small-text" : ""}`}>{option}</span>
            </label>
        ))}
    </div>
);

const Input = ({ hasError = false, placeholderText, setError, setValue, type = "text", value }) => (
    <div className="input-wrapper">
        <input
            className={`${value === "" ? "" : "active"} ${hasError ? "error" : ""} gt-america-regular-14-16`}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setError(false)}
            placeholder={placeholderText}
            type={type}
        />
        {hasError && (
            <Icon type={IconTypes.ERROR} />
        )}
    </div>
);

const SubmitButton = ({ handleClick, isLoading }) => (
    <div className={`submit-button ${isLoading ? "loading" : ""} gt-america-bold-15-24`} onClick={isLoading ? () => { } : handleClick}>
        {isLoading ? "Submitting..." : "Submit"}
    </div>
);

const SXMMediaForm = ({ completeForm, formErrors, formFields, formValues, isLoading, setFormErrors, setFormValues }) => {
    return (
        <div className="sxm-media-form">
            <div className="fields">
                {formFields.map(formField => (
                    <div className="field-wrapper" key={formField.key}>
                        <Input
                            hasError={formErrors[formField.key]}
                            placeholderText={`${formField.title}*`}
                            setError={() => setFormErrors({ ...formErrors, [formField.key]: false })}
                            setValue={(val) => setFormValues({ ...formValues, [formField.key]: val })}
                            type={formField.type || "text"}
                            value={formValues[formField.key]}
                        />
                    </div>
                ))}
            </div>
            <hr />
            <div className="bottom-row">
                <div className="left-col">
                    <div className="footnote gt-america-regular-7-9">By clicking submit, you agree that we may store the information you submit and that we may contact you using this information. You further agree that the information provided is that of a business or its representative and is not consumer information.</div>
                </div>
                <div className="right-col">
                    <SubmitButton isLoading={isLoading} handleClick={completeForm} />
                </div>
            </div>
        </div>
    );
};

const AdswizzForm = ({ completeForm, formErrors, formFields, formValues, isLoading, setFormErrors, setFormValues }) => {
    return (
        <div className="adswizz-form">
            <div className="columns">
                <div className="column">
                    <div className="section-title gt-america-regular-18-24">Information<RequiredStar /></div>
                    {formFields.map(formField => (
                        <Input
                            hasError={formErrors[formField.key]}
                            key={formField.key}
                            placeholderText={`${formField.title}*`}
                            setError={() => setFormErrors({ ...formErrors, [formField.key]: false })}
                            setValue={(val) => setFormValues({ ...formValues, [formField.key]: val })}
                            type={formField.type || "text"}
                            value={formValues[formField.key]}
                        />
                    ))}
                </div>
                <div className="column">
                    <div className="row">
                        <Dropdown
                            label="Country"
                            options={countries}
                            placeholderText="Please Select"
                            setValue={(val) => setFormValues({ ...formValues, country: val })}
                            value={formValues.country}
                        />
                    </div>
                    <div className="row">
                        <RadioSelect
                            label="Sales Country / Region"
                            options={["North America", "Latin America", "Europe", "Rest of World"]}
                            setValue={(val) => setFormValues({ ...formValues, salesCountryRegion: val })}
                            value={formValues.salesCountryRegion}
                        />
                    </div>
                </div>
                <div className="column">
                    <div className="row">
                        <Dropdown
                            label="Industry Type"
                            options={industryTypes}
                            placeholderText="Please Select"
                            setValue={(val) => setFormValues({ ...formValues, industryType: val })}
                            value={formValues.industryType}
                        />
                    </div>
                    <div className="row">
                        <RadioSelect
                            label="Area of Interest"
                            options={[
                                "I am a podcast publisher interested in publishing my content.",
                                "I am a podcaster interested in hosting and analytics.",
                                "I am an advertiser interested in digital audio or podcast advertising.",
                            ]}
                            setValue={(val) => setFormValues({ ...formValues, areaOfInterest: val })}
                            value={formValues.areaOfInterest}
                        />
                    </div>
                </div>
            </div>
            <hr />
            <div className="bottom-row">
                <div className="left-col">
                    <div className="footnotes gt-america-regular-7-9">
                        <div className="footnote-left">
                            <div>AdsWizz is committed to protecting and respecting your privacy, and we’ll only use your personal information to administer your account and to provide the products and services you requested from us. From time to time, we would like to contact you about our products and services, as well as other content that may be of interest to you. If you consent to us contacting you for this purpose, please tick below to say how you would like us to contact you:</div>
                            <div className="agree-to-terms">
                                <RadioSelect
                                    isCheckbox
                                    options={[
                                        <span>
                                            I agree to the terms and conditions stated in Adswizz’s{' '}
                                            <a href="https://www.adswizz.com/our-privacy-policy/" target="_blank">Privacy Policy</a>
                                            <RequiredStar />
                                        </span>
                                    ]}
                                    setValue={(val) => setFormValues({ ...formValues, agreeToTerms: val })}
                                    smallText
                                    value={formValues.agreeToTerms}
                                />
                            </div>
                        </div>
                        <div className="footnote-right">
                            <span>You can unsubscribe from these communications at any time. For more information on how to unsubscribe, our privacy practices, and how we are committed to protecting and respecting your privacy, please review our Privacy Policy.</span>
                            <br />
                            <span>By clicking submit, you consent to allow AdsWizz to store and process the personal information submitted above to provide you the content requested.</span>
                        </div>
                    </div>
                </div>
                <div className="right-col">
                    <SubmitButton isLoading={isLoading} handleClick={completeForm} />
                </div>
            </div>
        </div>
    );
};

const UnlockDataForm = ({ currentBrand, setShowUnlockDataForm }) => {
    let formFields = [];
    if (currentBrand === Brands.SXM_MEDIA) {
        formFields = [
            { title: "First Name", key: "firstName" },
            { title: "Last Name", key: "lastName" },
            { title: "Email Address", key: "emailAddress", type: "email" },
            { title: "Company Name", key: "companyName" },
            { title: "Job Title", key: "jobTitle" },
            { title: "Phone Number", key: "phoneNumber", type: "tel" },
        ];
    } else if (currentBrand === Brands.ADSWIZZ) {
        formFields = [
            { title: "First Name", key: "firstName" },
            { title: "Last Name", key: "lastName" },
            { title: "Business Email", key: "businessEmail", type: "email" },
            { title: "Company Name", key: "companyName" },
        ];
    }

    let defaultFormValues = {};
    if (currentBrand === Brands.SXM_MEDIA) {
        defaultFormValues = {
            firstName: "",
            lastName: "",
            emailAddress: "",
            companyName: "",
            jobTitle: "",
            phoneNumber: "",
        };
    } else if (currentBrand === Brands.ADSWIZZ) {
        defaultFormValues = {
            firstName: "",
            lastName: "",
            businessEmail: "",
            companyName: "",
            country: "",
            salesCountryRegion: "",
            industryType: "",
            areaOfInterest: "",
            agreeToTerms: false,
        };
    }

    let defaultFormErrors = {};
    if (currentBrand === Brands.SXM_MEDIA) {
        defaultFormErrors = {
            firstName: false,
            lastName: false,
            emailAddress: false,
            companyName: false,
            jobTitle: false,
            phoneNumber: false,
        };
    } else if (currentBrand === Brands.ADSWIZZ) {
        defaultFormErrors = {
            firstName: false,
            lastName: false,
            businessEmail: false,
            companyName: false,
            country: false,
            salesCountryRegion: false,
            industryType: false,
            areaOfInterest: false,
            agreeToTerms: false,
        };
    }

    const [formValues, setFormValues] = useState(defaultFormValues);
    const [formErrors, setFormErrors] = useState(defaultFormErrors);
    const [hasErrors, setHasErrors] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const formWrapperStyling = {};
    if (currentBrand === Brands.SXM_MEDIA) {
        formWrapperStyling.width = "calc(var(--grid-width-8-col) - 82px)";
    } else if (currentBrand === Brands.ADSWIZZ) {
        formWrapperStyling.width = "calc(var(--grid-width-10-col) - 82px)";
    }

    const handleFormSubmit = async () => {
        const formFieldKeys = Object.keys(formValues);

        const emptyRequiredFields = formFieldKeys
            .map(key => ({ key, isEmpty: !!!formValues[key] }))
            .filter(formField => formField.isEmpty)
            .map(formField => formField.key);

        // error state
        if (emptyRequiredFields.length > 0) {
            setHasErrors(true);
            // success state
        } else {
            setLoading(true);
            await sendFormSubmission(currentBrand, formValues);
            localStorage.setItem("formSubmitted", true);
            LinkedInTag.track("8539825");
            setShowUnlockDataForm(false);
            document.body.style.overflow = "unset";
            setLoading(false);
        }
    };

    return (
        <div className="unlock-data-form">
            <div className="container">
                <div className="form-wrapper" style={formWrapperStyling}>
                    <div className="form-header">
                        <div className="title gt-america-bold-48-48">Before you go any further</div>
                        <div className="subtitle gt-america-regular-26-31">Please fill out this form to unlock the data in this Trends Report.</div>
                        {hasErrors && (
                            <div className="error-message gt-america-regular-8-12">One or more required fields are empty.</div>
                        )}
                    </div>
                    {currentBrand === Brands.SXM_MEDIA && (
                        <SXMMediaForm
                            {...{ formErrors, formFields, formValues, isLoading, setFormErrors, setFormValues }}
                            completeForm={handleFormSubmit}
                        />
                    )}
                    {currentBrand === Brands.ADSWIZZ && (
                        <AdswizzForm
                            {...{ formErrors, formFields, formValues, isLoading, setFormErrors, setFormValues }}
                            completeForm={handleFormSubmit}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const Intro = ({ currentBrand, currentBreakpoint, introPodcastsSectionHeight, navbarHeight, scrollPos, windowSize }) => {
    const [logoBlockHover, setLogoBlockHover] = useState(null);
    const [formValues, setFormValues] = useState({ firstName: "", lastName: "", email: "" });
    const [verticalScroll, setVerticalScroll] = useState(0);
    const [fixedVerticalScollContent, setFixedVerticalScollContent] = useState(false);
    const [showUnlockDataForm, setShowUnlockDataForm] = useState(false);
    const [orangeRotation, setOrangeRotation] = useState(0);
    const [animImageScale, setAnimImageScale] = useState(1);
    const [animImageSlide, setAnimImageSlide] = useState(0);
    const horizontalScrollBlockRef = useRef(null);
    const contentRef = useRef(null);
    const bottomRowCoversRef = useRef(null);
    const detailsRef = useRef(null);
    const paragraphsRef = useRef(null);
    const freshWithDataRef = useRef(null);

    useEffect(() => {
        const currentRefPos = horizontalScrollBlockRef?.current?.offsetTop;
        const currentRefWidth = horizontalScrollBlockRef?.current?.clientWidth;
        const currentRefHeight = horizontalScrollBlockRef?.current?.clientHeight;
        const currentRefPosTopFromScreenTop = horizontalScrollBlockRef?.current?.getBoundingClientRect().top;
        const currentRefPosBottomFromScreenTop = horizontalScrollBlockRef?.current?.getBoundingClientRect().bottom;
        const paragraphsRefPos = paragraphsRef?.current?.offsetTop;

        // set vertical scroll for rows in podcast section
        if (currentRefPos && currentRefWidth && scrollPos && windowSize) {
            const screenTop = scrollPos;
            const screenBottom = screenTop + windowSize.height;
            const currentPercWithinScreen = currentRefPosTopFromScreenTop / 3000 * -1;

            // if section is below screen, keep at start
            if (currentRefPos > screenBottom) {
                setVerticalScroll(0);
            }

            // if section is above screen, keep at end
            else if (currentRefPos < (screenTop - currentRefHeight)) {
                setVerticalScroll(currentRefWidth);
            }

            // if section is within screen, slide section
            else {
                setVerticalScroll(currentPercWithinScreen);
            }
        }

        // set positioning for rows in podcast section
        if (currentRefPosTopFromScreenTop && currentRefPosBottomFromScreenTop && scrollPos && windowSize) {
            let bottomRowTopPos = 0;
            if (windowSize.width < 500) {
                bottomRowTopPos = 400;
            } else if (windowSize.width < 768) {
                bottomRowTopPos = 320;
            } else if (windowSize.width < 1368) {
                if (windowSize.height <= 860) {
                    bottomRowTopPos = 490;
                } else {
                    bottomRowTopPos = 438;
                }
            } else {
                if (windowSize.height <= 850) {
                    bottomRowTopPos = 383;
                } else if (windowSize.height <= 1020) {
                    bottomRowTopPos = 438;
                } else {
                    bottomRowTopPos = 570;
                }
            }

            if (scrollPos >= (currentRefPos + currentRefHeight)) {
                contentRef.current.style.position = "unset";
                bottomRowCoversRef.current.style.position = "unset";
                detailsRef.current.style.position = "unset";
            } else if (currentRefPosTopFromScreenTop < 200) {
                contentRef.current.style.position = "fixed";
                contentRef.current.style.top = "200px";
                contentRef.current.style.bottom = "auto";
                detailsRef.current.style.position = "absolute";
                detailsRef.current.style.left = "16px";
                bottomRowCoversRef.current.style.position = "absolute";
                bottomRowCoversRef.current.style.top = bottomRowTopPos + "px";
                bottomRowCoversRef.current.style.left = "0";
            } else {
                contentRef.current.style.position = "unset";
                bottomRowCoversRef.current.style.position = "unset";
                detailsRef.current.style.position = "unset";
            }
        }

        // lock form on screen until submitted when user scrolls down to paragraphs section
        if (paragraphsRefPos) {
            if (scrollPos > paragraphsRefPos) {
                const hasUserSubmittedForm = localStorage.getItem("formSubmitted") || false;
                if (!hasUserSubmittedForm) {
                    setShowUnlockDataForm(true);
                    document.body.style.overflow = "hidden";
                }
            }
        }

        // rotate "fresh with data" orange image on scroll
        if (freshWithDataRef?.current) {
            const currentImgPos = freshWithDataRef.current.getBoundingClientRect().top - navbarHeight;
            const startPos = windowSize.height * 0.3;
            const endPos = windowSize.height * -0.5;

            if (startPos <= currentImgPos) {
                setOrangeRotation(0);
            } else if (startPos > currentImgPos && currentImgPos > endPos) {
                const totalDist = startPos - endPos;
                const currDist = startPos - currentImgPos;
                setOrangeRotation(-45 * (currDist / totalDist));
            } else if (currentImgPos <= endPos) {
                setOrangeRotation(-45);
            }
        }

        // scale and slide 3d cover images on scroll
        if (scrollPos !== null && scrollPos < 2000) {
            setAnimImageScale(1 + (scrollPos / 2000));
            setAnimImageSlide(scrollPos / 10);
        }
    }, [scrollPos, windowSize]);

    let bottomRowStartPos = 100;
    if (windowSize.width < 500) {
        bottomRowStartPos = 500;
    } else if (windowSize.width < 768) {
        bottomRowStartPos = 320;
    } else if (windowSize.height <= 860) {
        bottomRowStartPos = 300;
    }

    return (
        <div className="intro">
            <div className="container">
                <div className="cover-image-wrapper">
                    <img className="cover-image" src={CoverImage} alt="" />
                    <div
                        className="heart-wrapper"
                        style={{ transform: `translate(${animImageSlide}px, -${animImageSlide}px)` }}
                    >
                        <img className="anim-image heart-3d" src={Heart} alt="" />
                    </div>
                    <div
                        className="headphones-wrapper"
                        style={{ transform: `translate(${animImageSlide}px, -${animImageSlide}px)` }}
                    >
                        <img className="anim-image headphones-3d" src={Headphones} alt="" />
                    </div>
                    <div
                        className="microphones-wrapper"
                        style={{ transform: `translate(-${animImageSlide}px, -${animImageSlide}px)` }}
                    >
                        <img className="anim-image microphones-3d" src={Microphones} alt="" />
                    </div>
                </div>
                <div className="thought-bubble-wrapper">
                    <ThoughtBubble
                        {...{ currentBreakpoint, scrollPos, windowSize }}
                        backgroundFill="gradient-rose-peach-2"
                        customContents={(
                            <div className="thought-bubble-text gt-america-bold-52-44">The <OutlineText outlineColor="neutral-white">2022</OutlineText> Podcast Trends Report</div>
                        )}
                        customPadding="24px 8px 56px 24px"
                        fitContentWidth
                        foregroundBgColor="neutral-black"
                        outlineColor="neutral-white"
                    />
                </div>
                <div className="presented-by gt-america-bold-13-12">
                    <span>Presented by </span>
                    <br />
                    <span className="sxm-media">SiriusXM Media</span>
                    <span> x </span>
                    <span className="adswizz">AdsWizz</span>
                    <span> x </span>
                    <span className="simplecast">Simplecast</span>
                </div>
                <div className="paragraphs" ref={paragraphsRef}>
                    <div className="row gt-america-regular-26-31">
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="column">
                            Podcasting is not the space you once knew.
                        </FadeInSlideUp>
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="column" waitSeconds={0.3}>
                            Topic-hopping listeners with a respect for ads.
                        </FadeInSlideUp>
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="column" waitSeconds={0.6}>
                            Ready-to-monetize creators who love wrestling (mhmm).
                        </FadeInSlideUp>
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="column" waitSeconds={0.9}>
                            Forward-thinking advertisers who are successfully implementing revolutionary adtech.
                        </FadeInSlideUp>
                    </div>
                    <FadeInSlideUp {...{ scrollPos, windowSize }} className="main-content">
                        <div className="left-col gt-america-regular-26-36">
                            <span>… Are all just the tip of the iceberg when it comes to the multitude of factors driving change in podcasting and pushing the boundaries of ad innovation in ways we could have never imagined.</span>
                            <br /><br />
                            <span>This space is unpredictable, unexpected, captivating, and we love it. From new listener profiles to the biggest missed opportunities for advertisers, here’s a fresh take on everything you need to know about podcasting today.</span>
                            <br /><br />
                            <span>This is the new new in podcasts.</span>
                        </div>
                        <div className="right-col">
                            <img src={currentBreakpoint === Breakpoints.MOBILE ? ParagraphsCoverImageMobile : ParagraphsCoverImage} alt="" />
                        </div>
                    </FadeInSlideUp>
                </div>
                <div className="hosts-wrapper">
                    <div className="hosts">
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="info">
                            <div className="title gt-america-bold-80-76">Your hosts</div>
                            <div className="description gt-america-regular-24-31">Together, we’re on a mission to create the most scalable, connected podcast ecosystem.</div>
                        </FadeInSlideUp>
                        <FadeInSlideUp {...{ scrollPos, windowSize }} className="logo-blocks">
                            {hosts.map((host, index) => {
                                const isHover = logoBlockHover === index;

                                return (
                                    <div
                                        className="logo-block"
                                        onMouseOver={() => setLogoBlockHover(index)}
                                        onMouseOut={() => setLogoBlockHover(null)}
                                        key={index}
                                    >
                                        <div className="top-layer" style={{
                                            zIndex: isHover ? -1 : 1,
                                            top: isHover ? 0 : -20,
                                            left: isHover ? 0 : 20,
                                        }}>
                                            {windowSize.width < 950 ? (
                                                <Logo brand={host.brand} height={hostLogoMobileHeights[host.brand]} />
                                            ) : (
                                                <Logo brand={host.brand} />
                                            )}
                                        </div>
                                        <div className="bottom-layer" style={{
                                            backgroundColor: `var(--color-${host.color})`,
                                            position: isHover ? "absolute" : "relative",
                                            top: isHover ? -20 : 0,
                                            left: isHover ? 20 : 0,
                                            width: isHover ? "calc(100% - 54px)" : (windowSize.width < 950 ? "calc(100% - 54px)" : "auto"),
                                        }}>
                                            <div className="left-col">
                                                <Logo brand={host.brand} />
                                            </div>
                                            <div className="right-col gt-america-regular-14-19">
                                                {host.text}
                                            </div>
                                        </div>
                                        <div className="vertical-spacer"></div>
                                    </div>
                                );
                            })}
                        </FadeInSlideUp>
                        {logoBlockHover === 0 && (
                            <div className="footnote gt-america-regular-7-20">
                                Internal monthly metrics from subscribers, active accounts and user logs
                            </div>
                        )}
                    </div>
                </div>
                <FadeInSlideUp {...{ scrollPos, windowSize }} className="fresh-with-data">
                    <div className="left-col" ref={freshWithDataRef}>
                        <img className="background-image" src={FreshWithDataBackground} alt="" />
                        <div className="data-stickers">
                            <img src={FreshDataSticker} alt="" />
                            <img src={LocallySourcedSticker} alt="" />
                        </div>
                        <div className="fresh-with-data-orange" style={{ transform: `rotate(${orangeRotation}deg)` }}>
                            <img className="base" src={FreshWithDataOrange} alt="" />
                            <img className="leaf" src={FreshWithDataLeaf} alt="" />
                        </div>
                    </div>
                    <div className="right-col">
                        <div className="text gt-america-regular-26-31">
                            This report was curated, researched, measured, and analyzed by our team of in-house experts. You won’t find this unique combination of insights from top podcasters and publishers combined with the largest pool of weekly U.S. listeners anywhere else.
                        </div>
                        <div className="footnote gt-america-regular-6-10">
                            Q4 2021 Stitcher,  NBC, Audiochuck Triton (Internal) metrics AND Podwave unique users from Adswizz logs
                        </div>
                        <div className="brand-sections">
                            {publishers.map((publisher, index) => (
                                <div className="brand-row" key={index}>
                                    <ThoughtBubble
                                        {...{ currentBreakpoint, scrollPos, windowSize }}
                                        backgroundFill={publisher.backgroundFill}
                                        customContents={(
                                            <div className="brand-title gt-america-bold-15-21">
                                                {publisher.title}
                                            </div>
                                        )}
                                        customPadding="4px 8px"
                                        fitContentWidth
                                        foregroundBgColor="neutral-black"
                                        outlineColor="neutral-white"
                                        pushAmount={8}
                                    />
                                    <div className="brand-logos">
                                        {publisher.brands.map((brand, brandIndex) => (
                                            <img key={brandIndex} src={brand} alt="" />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeInSlideUp>
            </div>
            <div className="obsessed">
                <img className="heart-3d" src={Heart} alt="" />
                <div className="ticker-wrapper">
                    <Ticker
                        {...{ currentBreakpoint, scrollPos, windowSize }}
                        backgroundColor="var(--color-neutral-dark-gray-40t)"
                        outlineColor="neutral-white"
                        text="#Obsessed"
                    />
                </div>
            </div>
            <div className="podcasts">
                <FadeInSlideUp {...{ scrollPos, windowSize }} className="info container">
                    <div className="title gt-america-bold-80-66">#Obsessed and it’s not just us.</div>
                    <div className="description gt-america-regular-24-33">Over the past year, podcasts have grown to new heights—and we’re not just talking numbers (which, by the way, are quite impressive). It’s clear that podcasting is a community, curating fandoms and a sense of devotion that you just don’t see in other mediums.</div>
                </FadeInSlideUp>
                <div
                    className="horizontal-scroll-block"
                    ref={horizontalScrollBlockRef}
                    style={{ height: introPodcastsSectionHeight }}
                >
                    <div className="content-wrapper" ref={contentRef}>
                        <img className="top-row-covers" src={TopRowPodcastCovers} alt="" style={{
                            transform: `translateX(calc(-${(verticalScroll * 4964 /* image width */ * 0.1)}px))`,
                            marginTop: -84,
                        }} />
                        <div className="details container" ref={detailsRef} style={{
                            transform: `translateX(calc(-${(verticalScroll) * (950 * 6) /* width and padding for each detail */ * 0.5}px + 10vw))`,
                        }}>
                            <div className="detail">
                                <div className="stat gt-america-bold-80-78">SiriusXM Media podcast audience grew <OutlineText outlineColor="neutral-black">123%</OutlineText> in the past year</div>
                                <div className="gt-america-regular-7-50">Triton Podcast Metrics Dec 2021 vs Dec 2020</div>
                            </div>
                            <div className="detail">
                                <div className="stat gt-america-bold-80-78"><OutlineText outlineColor="neutral-black">300,000</OutlineText> Years worth of listening in 2022</div>
                                <div className="gt-america-regular-7-50">Simplecast Internal Analytics 2021, based on 3.5M downloads</div>
                            </div>
                            <div className="detail">
                                <div className="stat gt-america-bold-80-78"><OutlineText outlineColor="neutral-black">110%</OutlineText> increase in podcast shows YoY</div>
                                <div className="gt-america-regular-7-50">The Podcast Index, 2022 vs April 2021</div>
                            </div>
                            <div className="detail">
                                <div className="stat gt-america-bold-80-78"><OutlineText outlineColor="neutral-black">157%</OutlineText> increase in ad spend by impressions in 2021</div>
                                <div className="gt-america-regular-7-50">AdsWizz Platform, Ad Spend by Impressions, Jan 2021–Dec 2021</div>
                            </div>
                            <div className="detail">
                                <div className="stat gt-america-bold-80-78"><OutlineText outlineColor="neutral-black">300%</OutlineText> YoY growth in publishers using AdsWizz adtech</div>
                                <div className="gt-america-regular-7-50">AdsWizz Platform, 2021 vs 2020</div>
                            </div>
                        </div>
                        <img className="bottom-row-covers" ref={bottomRowCoversRef} src={BottomRowPodcastCovers} style={{
                            transform: `translateX(calc(${verticalScroll * 4851 /* image width */ * 0.1}px - calc(4851px - ${bottomRowStartPos}vw)))`,
                        }} alt="Podcasts" />
                    </div>
                </div>
            </div>
            {showUnlockDataForm && (
                <UnlockDataForm {...{ currentBrand, setShowUnlockDataForm }} />
            )}
        </div>
    );
};
